import React, { useState, useEffect } from 'react';
import './LoginModal.css';
import { importKeysFromFile, exportKeysToFile, generateKeyPair } from '../utils/crypto';

const LoginModal = ({ isOpen, onGenerateKeys, onImportKeys, isLoading }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [importError, setImportError] = useState('');
  const [titleText, setTitleText] = useState('');
  const [separatorText, setSeparatorText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  // 打字机效果
  useEffect(() => {
    if (!isOpen) return;

    const titleTarget = 'ToxChat • Online';
    const separatorTarget = '----------------------------------------->';
    
    let titleIndex = 0;
    let separatorIndex = 0;
    
    const typeTitle = () => {
      if (titleIndex < titleTarget.length) {
        setTitleText(titleTarget.slice(0, titleIndex + 1));
        titleIndex++;
        setTimeout(typeTitle, 100);
      } else {
        // 标题完成后开始分隔符
        setTimeout(() => {
          const typeSeparator = () => {
            if (separatorIndex < separatorTarget.length) {
              setSeparatorText(separatorTarget.slice(0, separatorIndex + 1));
              separatorIndex++;
              setTimeout(typeSeparator, 80);
            }
          };
          typeSeparator();
        }, 500);
      }
    };

    // 光标闪烁效果
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    // 开始打字机效果
    setTimeout(typeTitle, 500);

    return () => {
      clearInterval(cursorInterval);
    };
  }, [isOpen]);

  const handleGenerateKeys = async () => {
    setIsGenerating(true);
    try {
      // 实际生成密钥对
      const keys = generateKeyPair();
      
      // 模拟生成过程
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 调用父组件的处理函数
      onGenerateKeys(keys);
    } catch (error) {
      alert('生成密钥失败: ' + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportError('');
    try {
      const keys = await importKeysFromFile(file);
      onImportKeys(keys);
    } catch (error) {
      setImportError(error.message);
    }
    event.target.value = '';
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay">
      <div className="login-modal">
        <div className="terminal-header">
          <div className="glowing-title">
            <span className="terminal-title">{titleText}</span>
            {showCursor && <span className="typing-cursor">_</span>}
          </div>
        </div>
        
        <div className="terminal-content">
          <div className="welcome-text">
            <p>Welcome to ToxChat Anonymous Encrypted Chat System</p>
            <p className="separator-line">
              {separatorText}
              {showCursor && <span className="typing-cursor">_</span>}
              <span className="smiley"> :)</span>
            </p>
            <br />
            <p>End-to-end encryption ensures your privacy and security</p>
            <p>Please choose your login method:</p>
            <br />
          </div>

          <div className="auth-options">
            <div className="auth-option">
              <h3>> Generate New Key Pair</h3>
              <p>Choose this option for first-time users</p>
              <button 
                className="terminal-button"
                onClick={handleGenerateKeys}
                disabled={isGenerating || isLoading}
              >
                {isGenerating ? 'Generating...' : isLoading ? 'Authenticating...' : 'Generate Keys'}
              </button>
              {(isGenerating || isLoading) && (
                <div className="generating-animation">
                  <span>{isGenerating ? 'Generating RSA-2048 key pair' : 'Authenticating with server'}</span>
                  <span className="cursor-blink">_</span>
                </div>
              )}
            </div>

            <div className="auth-option">
              <h3>> Import Existing Keys</h3>
              <p>Import previously exported key files</p>
              <label className="file-input-label">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileImport}
                  className="file-input"
                  disabled={isLoading}
                />
                {isLoading ? 'Authenticating...' : 'Select Key File'}
              </label>
              {importError && (
                <div className="error-message">
                  Error: {importError}
                </div>
              )}
            </div>
          </div>

          <div className="security-notice">
            <p>Security Notice:</p>
            <p>• Keep your key files secure</p>
            <p>• Lost keys cannot recover chat history</p>
            <p>• Regularly backup your key files</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
